<template>
  <div class="notification-type">
    <div
        v-for="(alertType, count) in options"
        :key="`${alertType.id}-${count}`"
    >
      <div class="notification-type__option" @click="selectType(alertType.id)">
        <v-icon
            size="1.2em"
            class="notification-type__option-icon"
            :color="type === alertType.id ? 'primary':'gray'"
        >
          {{ type === alertType.id ? 'fas fa-check-circle' : 'far fa-check-circle' }}
        </v-icon>
        <div>{{ alertType.name }}</div>
        <v-tooltip
            content-class="normal-tooltip text-center"
            bottom
        >
          <template v-slot:activator="{ on }">
            <div
                :class="count === 0 ? 'mb-1 ml-2' : 'ml-2'"
                v-on="on"
            >
              <v-icon
                  v-if="alertType.icon"
                  :color="alertType.iconColor"
                  class="notification-type__icon"
              >{{ alertType.icon }}
              </v-icon
              >
              <v-badge
                  v-else
                  content="1"
                  class="ml-1"
              ></v-badge>
            </div>
          </template>
          <span>{{ alertType.description }}</span>
        </v-tooltip>
      </div>
      <div v-if="(type === 'CONTINUOUS') && (count === 0)" class="dynamic-threshold-settings">
        <div class="dynamic-threshold-title">
          Dynamic Threshold
          <v-tooltip
              content-class="normal-tooltip text-left"
              bottom
          >
            <template v-slot:activator="{ on }">
              <div
                  :class="count === 0 ? 'mb-1 ml-2' : 'ml-2'"
                  v-on="on"
              >
                <v-icon
                    color="#2196F3"
                    class="notification-type__icon"
                    size="1.2em"
                >
                  fas fa-info-circle
                </v-icon>
              </div>
            </template>
            <div>
              Edit the Alert's notification type Dynamic Threshold settings.
            </div>
            <div>
              This specifies at which rate and value change conditions will trigger the alert
            </div>
          </v-tooltip>
        </div>
        <div class="pl-2 dynamic-threshold-inputs-cont">
          <input-wrapper-2
              label="Enabled"
              class="mb-8"
              full-width
          >
            <v-switch
                dense
                v-model="dynamicThreshold.enable"
                :label="dynamicThreshold.enable === true ? 'Yes':'No'"
                class="mt-0"
                hide-details
            ></v-switch>
          </input-wrapper-2>
          <v-text-field
              dense
              outlined
              persistent-placeholder
              label="Argument Name (starting point)"
              v-model="dynamicThreshold.argumentName"
              class="my-2"
          />
          <v-text-field
              dense
              outlined
              label="Next Level"
              v-model.number="dynamicThreshold.nextLevel"
              class="my-2"
          />
          <v-select
              ref="changeLevelTypeInputRef"
              v-model="dynamicThreshold.changeLevelType"
              dense
              outlined
              label="Level Type"
              :items="changeLevelTypeOptions"
              style="max-width: 300px"
              :rules="[specificationRules.required]"
          />
          <input-wrapper-2
              label="Change Direction Alert"
              dense
              full-width
          >
            <v-switch
                dense
                v-model="dynamicThreshold.changeDirectionAlert"
                :label="dynamicThreshold.changeDirectionAlert === true ? 'Yes':'No'"
                class="mt-0"
                hide-details
            ></v-switch>
          </input-wrapper-2>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InputWrapper2 from "@/components/UI/InputWrapper2.vue";

/**
 * @Specifications component
 * provides a user interface component to handle the specifications and the
 * Dynamic Threshold parameters/settings for TradeWatcher alerts notification
 */
export default {
  name: 'Specifications',
  components: {InputWrapper2},
  props: {
    value: {
      type: Object,
      default: () => {
        return {enable: false, nextLevel: 0, argumentName: '', changeLevelType: ''};
      },
    },
    type: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    notificationType: '',
    dynamicThreshold: {
      enable: false,
      nextLevel: 0,
      argumentName: '',
      changeLevelType: '',
      changeDirectionAlert: false,
    },
    changeLevelTypeOptions: [
      {value: 'AMOUNT', text: 'Amount'},
      {value: 'PERCENTAGE', text: 'Percentage'},
      {value: 'ABSOLUTE_AMOUNT', text: 'Absolute Amount'},
      {value: 'ABSOLUTE_PERCENTAGE', text: 'Absolute Percentage'},
    ],
    specificationRules: {
      required: (value) => !!value || "Required",
    },
  }),
  watch: {
    dynamicThreshold: {
      /**
       * Handle any changes on the Dynamic Threshold parameters
       * @param newValue {object} - the whole updated dynamic threshold parameters object
       */
      handler(newValue) {
        this.$emit('input', newValue);
      },
      deep: true,
    },
  },
  mounted() {
    /**
     * Handle the bound props value and transform
     * them accordingly
     */
    if (!!this.type) {
      this.notificationType = this.type;
    }
    if (!!this.value) {
      this.dynamicThreshold = {
        enable: this.value.enable,
        nextLevel: this.value.nextLevel,
        argumentName: this.value.argumentName,
        changeLevelType: this.value.changeLevelType,
        changeDirectionAlert: this.value.changeDirectionAlert,
      }
    }
  },
  methods: {
    /**
     * Handle the Notification type selection and emit the 'input' event to handle two-way binding
     * @param selection {string} - The string value of the notification type
     */
    selectType(selection) {
      // console.log('SELECTION', selection);
      this.$emit('change-type', selection);
    }
  }
}
</script>

<style lang="scss" scoped>
.notification-type {
  display: flex;
  flex-direction: column;
  row-gap: 8px;

  &__icon {
    font-size: 1em;
  }

  &__option {
    display: flex;
    flex-direction: row;
    align-items: center;
    cursor: pointer;
    transition: all ease-in-out 300ms;

    &:hover {
      font-weight: 600;
    }

    &-icon {
      margin-left: 12px;
      margin-right: 16px;
    }
  }

  .dynamic-threshold-settings {
    margin-left: 48px;
  }

  .dynamic-threshold-title {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: fit-content;
    font-weight: 600;
    //text-transform: uppercase;
    font-size: 0.8em;
    margin: 8px auto 12px 0;
    border-bottom: 1px solid #47bd96;
  }

  .dynamic-threshold-inputs-cont {
    display: flex;
    flex-direction: column;
  }
}
</style>